import { Modal } from "ant-design-vue";
import lodash from "lodash";
import { defineAsyncComponent } from "vue";
import { createFieldSelect } from "../../../../../components/input/template-builder/template-builder.logic";
import { createColumnDate, createColumnInput, createColumnSelect } from "../../../../../components/table/c-table.logic";
import i18n from "../../../../../i18n";
import store from "../../../../../store";
import { conditionFormChecker, putField } from "../../../../../utils/form";
import { createPage } from "../../../../../utils/page";
import { filterCompanyRoleMgt } from "../../role-mgt/role-mgt.section";
import { createFieldDesc } from "../tab-1/tab1.section";
import { callUserRequestStatus, callUserRequestType } from "../tab-4/tab4.section";
import { callUserAuthorize, cloneUserAuthorize, createFieldUserAuthorize, onLinkIdCard } from "../tab-5/tab5.section";

const AttachmentInlineList = defineAsyncComponent(() => import("../../../../../components/attachment/AttachmentInlineList"));

export const callRoleCompanySearch = ({ searchQuery, userObjectives, companyRoleMgts }) => {
    const where = [];
    if (searchQuery) {
        where.push({ name: "desc", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/security/role/companySearch", { criteria: createPage({ options: { size: 100, where } }) })
        .then(page =>
            filterCompanyRoleMgt({
                roles: page.data,
                userObjectives: userObjectives,
                companyRoleMgts: companyRoleMgts
            })
        )
        .then(items =>
            items.filter(item => {
                if (item.name.endsWith("_VIEW")) {
                    return false;
                }
                if (["HAZ_MGT", "FEE"].includes(item.name)) {
                    return false;
                }
                return true;
            })
        )
        .then(items => items.map(item => ({ refRole: item })));
};

export const createColumns = () => {
    return [
        createColumnInput({
            title: () => i18n.global.t("views.company.mgt.tab.6.title"),
            dataIndex: "refUserAuthorizeTemp.companyName",
            dataIndexValue: ({ record }) =>
                record.refUserAuthorizeTemp.refPersonType?.key === "1"
                    ? record.refUserAuthorizeTemp.companyName
                    : record.refUserAuthorizeTemp.fullName,
            customRender: ({ record, column }) => column.dataIndexValue({ record }),
            align: "center"
        }),
        createColumnInput({
            title: () => i18n.global.t("views.company.mgt.tab.6.docId"),
            dataIndex: "refUserAuthorizeTemp.idCard",
            dataIndexValue: ({ record }) =>
                record.refUserAuthorizeTemp.refPersonType?.key === "1"
                    ? `${i18n.global.t("views.register.idTax")} ${record.refUserAuthorizeTemp.idCard}`
                    : `${record.refUserAuthorizeTemp.refDocumentTypePerson?.value} ${record.refUserAuthorizeTemp.idCard}`,
            customRender: ({ record }) => {
                if (record.refUserAuthorizeTemp.refPersonType?.key === "1") {
                    return (
                        <div>
                            <div>{i18n.global.t("views.register.idTax")}</div>
                            <div>{record.refUserAuthorizeTemp.idCard}</div>
                        </div>
                    );
                } else if (record.refUserAuthorizeTemp.refPersonType?.key === "2") {
                    return (
                        <div>
                            <div>{record.refUserAuthorizeTemp.refDocumentTypePerson?.value}</div>
                            <div>{record.refUserAuthorizeTemp.idCard}</div>
                        </div>
                    );
                }
                return <div></div>;
            },
            align: "center"
        }),
        createColumnDate({
            title: () => i18n.global.t("views.company.mgt.tab.approveDate"),
            dataIndex: "lastStatus.approve.0.statusDatetime",
            align: "center",
            width: 130
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.userRequest.refRequestType"),
            dataIndex: "refRequestType.value",
            inputProp: () => ({ list: callUserRequestType, optLabel: "value" }),
            search: { name: "refRequestType.id" },
            align: "center",
            width: 110
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.userRequest.refStatus"),
            dataIndex: "refStatus.value",
            inputProp: () => ({ list: callUserRequestStatus, optLabel: "value" }),
            search: { name: "refStatus.id" },
            align: "center",
            width: 100
        })
    ];
};

export const createField = ({ props, data, refs, key, isLoading }) => {
    const { viewOnly, menuContext } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            ...createFieldDesc({ props, data, key, isLoading }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refRequestType`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.userRequest.refRequestType"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refRequestType,
                            "onUpdate:value": value => {
                                const oldValue = model.refRequestType;
                                model.refRequestType = value;
                                if (
                                    (["2"].includes(oldValue?.key) || (!["1"].includes(oldValue?.key) && ["1"].includes(value?.key))) &&
                                    model.refUserAuthorize
                                ) {
                                    Modal.confirm({
                                        title: i18n.global.t("views.request.modal.change", {
                                            text: i18n.global.t("model.userRequest.refRequestType")
                                        }),
                                        autoFocusButton: "cancel",
                                        onOk: () => {
                                            if (["1"].includes(value?.key)) {
                                                const { createForm } = require("./tab6.logic");
                                                cloneUserAuthorize({
                                                    model: {
                                                        ...createForm({ props, data, refs }),
                                                        id: model.id,
                                                        version: model.version,
                                                        isActive: model.isActive,
                                                        refStatus: model.refStatus,
                                                        refRequestDoc: model.refRequestDoc
                                                    }
                                                });
                                            } else {
                                                cloneUserAuthorize({ model, id: model.refUserAuthorize?.id });
                                            }
                                        },
                                        onCancel: () => {
                                            model.refRequestType = oldValue;
                                        }
                                    });
                                }
                            },
                            disabled: viewOnly,
                            list: callUserRequestType,
                            optLabel: "value",
                            allowClear: false
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refUserAuthorize`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("views.company.mgt.tab.6.title"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refUserAuthorize,
                            "onUpdate:value": value => {
                                const oldValue = model.refUserAuthorize;
                                model.refUserAuthorize = value;
                                if (oldValue) {
                                    Modal.confirm({
                                        title: i18n.global.t("views.request.modal.change", { text: i18n.global.t("views.company.mgt.tab.6.title") }),
                                        autoFocusButton: "cancel",
                                        onOk: () => cloneUserAuthorize({ model, id: value?.id }),
                                        onCancel: () => {
                                            model.refUser = oldValue;
                                        }
                                    });
                                } else {
                                    cloneUserAuthorize({ model, id: value?.id });
                                }
                            },
                            disabled: viewOnly,
                            list: ({ searchQuery }) =>
                                callUserAuthorize({
                                    searchQuery,
                                    parentUsername: store.state.resource.auth.info.user.username,
                                    authorizeTypeKey: "2"
                                }),
                            renderLabel: ({ item }) => {
                                if (item.refPersonType?.key === "1") {
                                    return <div>{item.companyName}</div>;
                                } else if (item.refPersonType?.key === "2") {
                                    return <div>{item.fullName}</div>;
                                }
                                return <div></div>;
                            },
                            allowClear: false
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.fullName`) }
            }),
            ...createFieldUserAuthorize({
                props,
                data,
                key: `${prefix}refUserAuthorizeTemp`,
                isLoading,
                onLinkIdCard: onLinkIdCard({ props, data, refs, isLoading })
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refUserAuthorizeTemp.roles`,
                colProp: () => ({ xs: 24, md: 20 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.role.modelName"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refUserAuthorizeTemp.roles,
                            "onUpdate:value": value => (model.refUserAuthorizeTemp.roles = value),
                            disabled: viewOnly,
                            list: ({ searchQuery }) =>
                                callRoleCompanySearch({ searchQuery, userObjectives: data.userObjectives, companyRoleMgts: data.companyRoleMgts }),
                            renderLabel: ({ item }) => <span>{item.refRole.desc}</span>,
                            mode: "multiple",
                            optKey: "refRole.id"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValueChildren: ({ record }) => record.refRole?.name }
            }),
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24 }, wrapperCol: { xs: 24 } }),
                inputRender: () => {
                    const componentProp = lodash.merge(
                        {
                            menuContext: menuContext,
                            viewOnly: viewOnly,
                            dataProp: data,
                            itemsKey: `${prefix}refUserAuthorizeTemp.attachments`,
                            canCreate: !viewOnly,
                            canDelete: !viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { componentKey: "AttachmentInlineList" } })
                    );
                    return <AttachmentInlineList {...componentProp}></AttachmentInlineList>;
                }
            }
        ]
    });
};
